<template>
  <section>
    <form method="post" @submit.prevent="send" novalidate>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ $t('attributes.add') + $t('attributes.country') }}
          </p>
        </header>
        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <form-group
              horizontal
              name="code"
              attribute="attributes.country_template"
              :label="$t('message.select') + $t('attributes.country_template')"
            >
              <b-autocomplete
                ref="airlineSelect"
                v-model="selectedCountryTemplate"
                name="code"
                field="display_name"
                :data="countryTemplates"
                :placeholder="
                  $t('message.please_select') +
                    $t('attributes.country_template')
                "
                :open-on-focus="true"
                clearable
                @typing="onSelectingCountryTemplate"
                @select="onSelectedCountryTemplate"
              >
                <template #default="props">
                  <div class="media">
                    <div class="media-left" style="width: 20px">
                      {{ props.option.code }}
                    </div>
                    <div class="media-content">
                      {{ props.option.name }}({{ props.option.english_name }})
                    </div>
                  </div>
                </template>
                <template #empty>
                  No results found
                </template>
              </b-autocomplete>
            </form-group>

            <form-group
              horizontal
              name="area_code"
              attribute="attributes.area"
              :label="$t('attributes.area')"
            >
              <b-select
                v-model="selectedArea"
                name="area_code"
                :loading="isLoading"
                :placeholder="
                  $t('message.please_select') + $t('attributes.area')
                "
                expanded
                @input="onSelectArea"
              >
                <option value="" disabled>
                  {{ $t('message.please_select') + $t('attributes.area') }}
                </option>
                <option
                  v-for="option in areas"
                  :key="option.code"
                  :value="option"
                >
                  {{ option.name }}
                </option>
              </b-select>
            </form-group>

            <form-group
              horizontal
              name="status"
              attribute="attributes.status"
              :label="$t('attributes.status')"
            >
              <b-switch v-model="form.status" name="status"></b-switch>
            </form-group>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button :loading="isSending" @click="$parent.close()">
            {{ $t('attributes.cancel') }}
          </b-button>
          <b-button
            :loading="isSending || isLoading"
            type="is-primary"
            native-type="submit"
          >
            {{ $t('attributes.add') }}
          </b-button>
        </footer>

        <b-loading :is-full-page="false" :active.sync="isLoading"></b-loading>
      </div>
    </form>
  </section>
</template>

<script>
import { debounce } from 'lodash'
import { required } from 'vuelidate/lib/validators'
import { AREA_API, COUNTRY_TEMPLATES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'CountryCreateForm',

  props: {
    isSending: {
      type: Boolean,
      default: false
    },
    country: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      countryTemplates: [],
      selectedCountryTemplate: null,

      areas: [],
      selectedArea: null,

      form: {
        id: this.country.id,
        area_code: this.country.area_code,
        code: this.country.code,
        status: this.country.status
      }
    }
  },
  validations: {
    form: {
      area_code: { required },
      code: { required },
      status: { required }
    }
  },
  mounted() {
    this.loadAreas()
  },
  methods: {
    async loadAreas() {
      try {
        this.isLoading = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.selectedArea = null

        if (this.form.area_code) {
          this.selectedArea = this.areas.find(
            a => a.code === this.form.area_code
          )
          this.form.area_code = this.selectedArea.code
        }
      } catch (e) {
        this.areas = []
        this.selectedArea = null

        notifyService.fetch_error('area', e)
      } finally {
        this.isLoading = false
      }
    },
    onSelectArea(option) {
      this.form.area_code = option && option.code
    },
    async getCountryTemplates(search) {
      const api = `${COUNTRY_TEMPLATES_API}/list`
      const params = {
        search: search
      }

      try {
        const res = await this.axios.get(api, { params })
        this.countryTemplates = res.data.data.map(x => ({
          ...x,
          code: x.code.trim(),
          name: x.name.trim(),
          english_name: x.english_name ? x.english_name.trim() : '',
          display_name: this.formatCountryName(x)
        }))
      } catch (e) {
        this.countryTemplates = []

        notifyService.fetch_error('country_template', e)
      }
    },
    formatCountryName(template) {
      if (!template) {
        return ''
      }

      return (
        `${template.code.trim()} - ${template.name.trim()}` +
        `(${template.english_name ? template.english_name.trim() : ''})`
      )
    },
    onSelectingCountryTemplate: debounce(function(search) {
      this.getCountryTemplates(search)
    }, 500),
    onSelectedCountryTemplate(option) {
      this.form.code = option && option.code
    },
    send() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
